import { type MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from "~/components/ui";
import { useDipLifecycle } from "~/features/dataImportProjects/context";

type LifecycleAction = {
  type: "activate" | "deactivate";
  projectId: string;
};

export function DipLifecycleAction() {
  const { t } = useTranslation();
  const { projectId, projectName, isReadOnly, processing, activate, deactivate } = useDipLifecycle();
  const [action, setAction] = useState<LifecycleAction | null>(null);
  const actionMatchesLifecycle =
    action?.type === "activate" ? isReadOnly : action?.type === "deactivate" ? !isReadOnly : false;
  const actionIsCurrent = action?.projectId === projectId && actionMatchesLifecycle;
  const isDeactivate = action?.type === "deactivate";

  useEffect(() => {
    setAction((current) => {
      if (!current) return current;

      const matchesLifecycle = current.type === "activate" ? isReadOnly : !isReadOnly;
      return current.projectId === projectId && matchesLifecycle ? current : null;
    });
  }, [isReadOnly, projectId]);

  const handleConfirm = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!action || processing) return;

    const matchesLifecycle = action.type === "activate" ? isReadOnly : !isReadOnly;
    if (action.projectId !== projectId || !matchesLifecycle) {
      setAction(null);
      return;
    }

    const succeeded = action.type === "activate" ? await activate() : await deactivate();
    if (succeeded) setAction(null);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={processing}
        onClick={() => setAction({ type: isReadOnly ? "activate" : "deactivate", projectId })}
      >
        {isReadOnly ? t("ui.activate") : t("ui.deactivate")}
      </Button>
      <AlertDialog
        open={action !== null && actionIsCurrent}
        onOpenChange={(open) => {
          if (!open && !processing) setAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isDeactivate ? t("ui.deactivateX", { x: projectName }) : t("ui.activateX", { x: projectName })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isDeactivate
                ? t("feature.dataImportProjects.v2.lifecycle.confirmDeactivate", { name: projectName })
                : t("feature.dataImportProjects.v2.lifecycle.confirmActivate", { name: projectName })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction disabled={processing} onClick={handleConfirm}>
              {isDeactivate ? t("ui.deactivate") : t("ui.activate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
